/**
 * 本机借看防护（ADR-0014）：只在浏览器里挡住界面，不加密缓存，也不替代服务端令牌鉴权。
 * 系统验证可能使用人脸、指纹或设备密码；这里不保存私钥，不进行服务端验签。
 */
export const GATE_KEY = 'star-sea/gate';

export type GateState = 'disabled' | 'locked' | 'unlocked' | 'invalid';

export interface GateStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** 测试只替换系统边界，不需要操作真实验证器。 */
export interface GateEnvironment {
  readonly origin: string;
  readonly rpId: string;
  readonly secure: boolean;
  readonly topLevel: boolean;
  readonly storage: GateStorage;
  randomBytes(length: number): Uint8Array<ArrayBuffer>;
  sha256(value: Uint8Array<ArrayBuffer>): Promise<ArrayBuffer>;
  available(): Promise<boolean>;
  create(options: CredentialCreationOptions): Promise<unknown>;
  get(options: CredentialRequestOptions): Promise<unknown>;
}

export interface LocalGate {
  readonly state: GateState;
  /** 已建好候选凭证，下一次点击 enable 执行实际解锁验证，成功后才保存开关。 */
  readonly enrolling: boolean;
  readonly busy: boolean;
  supported(): Promise<boolean>;
  /** 第一次点击创建后返回 false；第二次点击验证、保存并返回 true。 */
  enable(): Promise<boolean>;
  unlock(): Promise<void>;
  /** 即使当前已解锁，关闭门也需要重新验证。 */
  disable(): Promise<void>;
  /** 立即失效正在进行的验证；后台时也要调用，以取消尚未完成的启用。 */
  lock(): void;
  /** 仅随退出并清理令牌/缓存使用；不能作为保留登录数据的跳过入口。 */
  reset(): void;
  /** storage 事件（key 是 GATE_KEY 或 null）触发；配置有变化立即重锁。 */
  refresh(): void;
}

export interface GateOptions {
  environment?: GateEnvironment;
  onChange?: () => void;
}

interface Configuration {
  version: 1;
  credentialId: string;
  rpId: string;
  userHandle: string;
}

export class GateError extends Error {
  readonly code: 'unsupported' | 'cancelled' | 'invalid' | 'storage' | 'busy' | 'verification';
  constructor(code: GateError['code'], message: string) {
    super(message);
    this.name = 'GateError';
    this.code = code;
  }
}

function browserEnvironment(): GateEnvironment {
  return {
    origin: location.origin,
    rpId: location.hostname,
    secure: globalThis.isSecureContext,
    topLevel: window.top === window,
    // 在读写时才取 localStorage，让浏览器拒绝访问的异常也走关闭门的失败处理。
    storage: {
      getItem: (key) => localStorage.getItem(key),
      setItem: (key, value) => localStorage.setItem(key, value),
      removeItem: (key) => localStorage.removeItem(key),
    },
    randomBytes: (length) => crypto.getRandomValues(new Uint8Array(length)),
    sha256: (value) => crypto.subtle.digest('SHA-256', value),
    available: async () => typeof PublicKeyCredential !== 'undefined' && !!navigator.credentials && (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()),
    create: (options) => navigator.credentials.create(options),
    get: (options) => navigator.credentials.get(options),
  };
}

function encode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

function decode(text: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]+$/.test(text) || text.length > 2048) throw new Error('凭证编码不正确');
  const bytes = Uint8Array.from(atob(text.replaceAll('-', '+').replaceAll('_', '/') + '='.repeat((4 - (text.length % 4)) % 4)), (c) => c.charCodeAt(0));
  if (encode(bytes) !== text) throw new Error('凭证编码不正确');
  return bytes;
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function buffer(value: unknown, min: number, max: number): value is ArrayBuffer {
  return value instanceof ArrayBuffer && value.byteLength >= min && value.byteLength <= max;
}

function parseConfiguration(raw: string, rpId: string): Configuration {
  const value: unknown = JSON.parse(raw);
  if (!record(value) || value.version !== 1 || value.rpId !== rpId || typeof value.credentialId !== 'string' || typeof value.userHandle !== 'string') throw new Error('门配置不正确');
  if (decode(value.credentialId).length > 1024 || decode(value.userHandle).length !== 32) throw new Error('门配置不正确');
  return { version: 1, credentialId: value.credentialId, rpId, userHandle: value.userHandle };
}

function credential(value: unknown, challenge: Uint8Array, type: 'webauthn.create' | 'webauthn.get', origin: string): { id: string; response: Record<string, unknown> } {
  const bad = () => new GateError('verification', '设备验证的回应不匹配，请重新验证');
  if (!record(value) || value.type !== 'public-key' || !buffer(value.rawId, 1, 1024) || typeof value.id !== 'string' || value.id !== encode(new Uint8Array(value.rawId))) throw bad();
  if (!record(value.response) || !buffer(value.response.clientDataJSON, 1, 8192)) throw bad();
  let client: unknown;
  try {
    client = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(value.response.clientDataJSON));
  } catch {
    throw bad();
  }
  if (!record(client) || client.type !== type || client.challenge !== encode(challenge) || client.origin !== origin || (client.crossOrigin !== undefined && client.crossOrigin !== false) || client.topOrigin !== undefined) throw bad();
  return { id: value.id, response: value.response };
}

/** 系统 API 自身的取消、超时、不支持都不能变成解锁成功。 */
function errorOf(error: unknown): GateError {
  if (error instanceof GateError) return error;
  if (record(error) && (error.name === 'AbortError' || error.name === 'NotAllowedError')) return new GateError('cancelled', '验证已取消或超时，可以再点一次');
  if (record(error) && (error.name === 'NotSupportedError' || error.name === 'SecurityError')) return new GateError('unsupported', '这台设备暂时不能完成验证，请使用支持设备验证的 HTTPS 浏览器');
  return new GateError('verification', '设备验证没有完成，请重试');
}

export function createLocalGate(options: GateOptions = {}): LocalGate {
  const env = options.environment ?? browserEnvironment();
  let state: GateState = 'disabled';
  let config: Configuration | null = null;
  let candidate: Configuration | null = null;
  let raw: string | null | undefined;
  let operation: { controller: AbortController; raw: string | null | undefined } | null = null;
  let lastNotice = '';
  const notice = () => {
    const next = `${state}:${candidate?.credentialId ?? ''}:${operation !== null}`;
    if (next === lastNotice) return;
    lastNotice = next;
    options.onChange?.();
  };
  const abort = () => {
    operation?.controller.abort();
    operation = null;
  };
  const read = (initial = false) => {
    let next: string | null;
    try {
      next = env.storage.getItem(GATE_KEY);
    } catch {
      abort();
      candidate = null;
      config = null;
      raw = undefined;
      state = 'invalid';
      if (!initial) notice();
      return;
    }
    if (!initial && next === raw) return;
    abort();
    candidate = null;
    config = null;
    // 别的页面移除了门配置时，此页内存里还可能有登录资料，不能直接露出它们。
    const removedElsewhere = !initial && next === null && (raw !== null || state !== 'disabled');
    raw = next;
    if (next === null) state = removedElsewhere ? 'invalid' : 'disabled';
    else {
      try {
        config = parseConfiguration(next, env.rpId);
        state = 'locked';
      } catch {
        state = 'invalid';
      }
    }
    if (!initial) notice();
  };
  read(true);
  lastNotice = `${state}::false`;

  const begin = () => {
    read();
    if (operation) throw new GateError('busy', '正在等待设备验证');
    if (state === 'invalid') throw new GateError('invalid', '本机门配置无法读取，请退出并重新配对');
    if (!env.secure || !env.topLevel) throw new GateError('unsupported', '请在 HTTPS 页面中直接打开星海，再使用设备验证');
    const current = { controller: new AbortController(), raw };
    operation = current;
    notice();
    return current;
  };
  const check = (current: NonNullable<typeof operation>) => {
    if (operation !== current || current.controller.signal.aborted) throw new GateError('cancelled', '页面已锁定，请重新验证');
    let stored: string | null;
    try {
      stored = env.storage.getItem(GATE_KEY);
    } catch {
      read();
      throw new GateError('storage', '无法读取本机门配置，请退出并重新配对');
    }
    if (stored !== current.raw) {
      read();
      throw new GateError('cancelled', '本机门配置已变化，请重新验证');
    }
  };
  const finish = (current: NonNullable<typeof operation>) => {
    if (operation === current) operation = null;
    notice();
  };
  const persist = (next: Configuration | null) => {
    const serialized = next ? JSON.stringify(next) : null;
    try {
      if (serialized === null) env.storage.removeItem(GATE_KEY);
      else env.storage.setItem(GATE_KEY, serialized);
      if (env.storage.getItem(GATE_KEY) !== serialized) throw new Error('写入没有保留');
    } catch {
      abort();
      candidate = null;
      config = null;
      raw = undefined;
      state = 'invalid';
      notice();
      throw new GateError('storage', '无法保存本机门配置，尚未完成这次操作');
    }
    raw = serialized;
    config = next;
    candidate = null;
  };
  const verify = async (target: Configuration, current: NonNullable<typeof operation>) => {
    const challenge = env.randomBytes(32);
    // 必须在点击调用栈里发起 get；RP hash 等异步计算留到系统回应后。
    const result = await env.get({
      publicKey: {
        challenge,
        rpId: target.rpId,
        allowCredentials: [{ type: 'public-key', id: decode(target.credentialId), transports: ['internal'] }],
        userVerification: 'required',
        timeout: 60_000,
      },
      signal: current.controller.signal,
    });
    check(current);
    const returned = credential(result, challenge, 'webauthn.get', env.origin);
    if (returned.id !== target.credentialId) throw new GateError('verification', '回应来自另一把钥匙，请重新验证');
    const auth = returned.response.authenticatorData;
    if (!buffer(auth, 37, 65536) || !buffer(returned.response.signature, 1, 16384)) throw new GateError('verification', '设备验证的回应不完整，请重试');
    const bytes = new Uint8Array(auth);
    if ((bytes[32] & 0x05) !== 0x05) throw new GateError('verification', '设备没有确认是你，请重新验证');
    const user = returned.response.userHandle;
    if (user !== null && user !== undefined && (!buffer(user, 1, 64) || encode(new Uint8Array(user)) !== target.userHandle)) throw new GateError('verification', '回应来自另一位用户，请重新验证');
    const rpHash = new Uint8Array(await env.sha256(new TextEncoder().encode(target.rpId)));
    check(current);
    if (rpHash.length !== 32 || !rpHash.every((byte, i) => byte === bytes[i])) throw new GateError('verification', '设备验证的站点不匹配，请重试');
  };

  return {
    get state() { return state; },
    get enrolling() { return candidate !== null; },
    get busy() { return operation !== null; },
    async supported() {
      try {
        return env.secure && env.topLevel && (await env.available());
      } catch {
        return false;
      }
    },
    async enable() {
      const current = begin();
      try {
        if (state !== 'disabled') throw new GateError('invalid', '本机门已经启用');
        if (candidate) {
          const target = candidate;
          await verify(target, current);
          check(current);
          persist(target);
          state = 'unlocked';
          return true;
        }
        const challenge = env.randomBytes(32), userHandle = env.randomBytes(32);
        const result = await env.create({
          publicKey: {
            challenge,
            rp: { id: env.rpId, name: '星海画廊' },
            user: { id: userHandle, name: '星海本机解锁', displayName: '星海本机解锁' },
            pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
            authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required', residentKey: 'discouraged' },
            attestation: 'none',
            timeout: 60_000,
          },
          signal: current.controller.signal,
        });
        check(current);
        const returned = credential(result, challenge, 'webauthn.create', env.origin);
        candidate = { version: 1, credentialId: returned.id, rpId: env.rpId, userHandle: encode(userHandle) };
        return false;
      } catch (error) {
        throw errorOf(error);
      } finally {
        finish(current);
      }
    },
    async unlock() {
      const current = begin();
      try {
        if (state === 'disabled') return;
        if (!config) throw new GateError('invalid', '本机门配置不完整，请退出并重新配对');
        await verify(config, current);
        check(current);
        state = 'unlocked';
      } catch (error) {
        throw errorOf(error);
      } finally {
        finish(current);
      }
    },
    async disable() {
      const current = begin();
      try {
        if (state === 'disabled') {
          candidate = null;
          return;
        }
        if (!config) throw new GateError('invalid', '本机门配置不完整，请退出并重新配对');
        await verify(config, current);
        check(current);
        persist(null);
        state = 'disabled';
      } catch (error) {
        throw errorOf(error);
      } finally {
        finish(current);
      }
    },
    lock() {
      read();
      abort();
      candidate = null;
      if (state === 'unlocked') state = 'locked';
      notice();
    },
    reset() {
      abort();
      candidate = null;
      persist(null);
      state = 'disabled';
      notice();
    },
    refresh: () => read(),
  };
}
